// Detects catalog-name collisions between the catalogs a user is requesting and
// the catalogs that already exist on the selected Databricks workspace.
//
// Catalogs are created with a full name of `<prefix><business name>` (see
// namingConvention.ts). A requested catalog is treated as already existing when
// EITHER that derived full name OR the bare business name the user typed matches
// an existing catalog on the workspace (case-insensitive). The derived name is
// the authoritative "create if not exists" check; the bare-name check is a
// secondary guard so an obvious collision is never missed.
//
// The lookup itself is async and runs in the field component; the synchronous
// scaffolder validation callback cannot fetch, so the component publishes the
// result into the shared store below and the callback reads it.

export interface CatalogEntryLike {
  name: string;
  schemas?: string[];
}

export interface CatalogNameConflict {
  index: number;
  // The existing catalog name that matched: either the derived full name
  // (`prefix + business name`) or the bare business name the user typed.
  fullName: string;
}

export const catalogNameConflictMessage = (fullName: string): string =>
  `A catalog named "${fullName}" already exists on this workspace. ` +
  `Please choose a different name.`;

// Returns one conflict per requested catalog that already exists on the
// workspace. A catalog is considered to already exist when EITHER its derived
// full name (`prefix + business name`, the name that will actually be created)
// OR the bare business name the user typed matches an existing catalog name
// (exact match, case-insensitive). The normalized name is checked first, and
// the matched existing name is reported so the message is accurate.
export const findCatalogNameConflicts = (
  catalogs: CatalogEntryLike[] | undefined,
  prefix: string | string[],
  existingFullNames: string[],
): CatalogNameConflict[] => {
  if (!catalogs?.length) return [];

  const existing = new Set(
    existingFullNames
      .filter(name => typeof name === 'string' && name.length > 0)
      .map(name => name.toLowerCase()),
  );

  const conflicts: CatalogNameConflict[] = [];
  catalogs.forEach((catalog, index) => {
    const business = catalog?.name?.trim();
    if (!business) return;

    const derivedFullNames = (Array.isArray(prefix) ? prefix : [prefix]).map(
      value => `${value}${business}`,
    );
    // Check the normalized name first, then fall back to the raw typed name.
    const matchedName = [...derivedFullNames, business].find(candidate =>
      existing.has(candidate.toLowerCase()),
    );

    if (matchedName) {
      conflicts.push({ index, fullName: matchedName });
    }
  });

  return conflicts;
};

export type CatalogLookupStatus = 'idle' | 'loading' | 'ok' | 'error';

export interface KnownCatalogState {
  // The workspace the state describes; used to ignore stale reads once the user
  // switches workspace.
  workspace?: string;
  prefix: string;
  workspaces?: string[];
  prefixes?: string[];
  existingNames: string[];
  status: CatalogLookupStatus;
}

// Shared store of the catalogs known to exist on the currently-selected
// workspace. The field component populates it after fetching; the synchronous
// scaffolder validation callback reads it.
let knownCatalogState: KnownCatalogState = {
  workspace: undefined,
  prefix: '',
  workspaces: [],
  prefixes: [],
  existingNames: [],
  status: 'idle',
};

export const setKnownCatalogState = (state: KnownCatalogState): void => {
  knownCatalogState = state;
};

export const getKnownCatalogState = (): KnownCatalogState => knownCatalogState;

export const resetKnownCatalogState = (): void => {
  knownCatalogState = {
    workspace: undefined,
    prefix: '',
    existingNames: [],
    status: 'idle',
  };
};

// Central blocking decision shared by the field extensions. Returns the list of
// validation error messages that must block the wizard for the given requested
// catalogs, based on the current lookup result in the shared store.
//
// Fails closed: if a workspace is selected but the existing-catalog lookup has
// not succeeded, a single blocking error is returned so we never silently allow
// a name that might already exist.
export const collectCatalogConflictErrors = (
  catalogs: CatalogEntryLike[] | undefined,
): string[] => {
  const state = getKnownCatalogState();
  const errors: string[] = [];

  for (const catalog of catalogs ?? []) {
    if (!catalog.schemas?.length) {
      errors.push('At least one schema is required for each catalog.');
    }
    for (const schema of catalog.schemas ?? []) {
      if (!/^[a-z][a-z0-9_]{1,62}$/.test(schema)) {
        errors.push(
          `Schema name "${schema}" is invalid. Use lowercase letters, numbers, ` +
            'and underscores; start with a letter and use 2 to 63 characters.',
        );
      }
    }
  }

  if (
    (state.workspace || state.workspaces?.length) &&
    state.status !== 'ok'
  ) {
    return [
      ...errors,
      'Could not verify existing catalogs on the selected workspace. ' +
        'Please retry before submitting.',
    ];
  }

  return [
    ...errors,
    ...findCatalogNameConflicts(
      catalogs,
      state.prefixes?.length ? state.prefixes : state.prefix,
      state.existingNames,
    ).map(conflict => catalogNameConflictMessage(conflict.fullName)),
  ];
};
